import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import './styles/global.css';
import styles from './App.module.css';

import { Login } from './pages/Login/Login';
import { LinkedInCallback } from './pages/Login/LinkedInCallback';
import { ProjectSelection } from './pages/ProjectSelection/ProjectSelection';
import { Header } from './components/layout/Header/Header';
import { Sidebar } from './components/layout/Sidebar/Sidebar';
import { DashboardModule } from './components/dashboard/DashboardModule';
import { SentimentModule } from './components/sentiment/SentimentModule';
import { PersonasModule } from './components/personas/PersonasModule';
import { StrategyModule } from './components/strategy/StrategyModule';
import { MiddelenModule } from './components/middelen/MiddelenModule';
import { WerkplanningModule } from './components/werkplanning/WerkplanningModule';
import { VragenModule } from './components/vragen/VragenModule';
import { RelatiebeheerModule } from './components/relatiebeheer/RelatiebeheerModule';
import { GebiedsanalyseModule } from './components/gebiedsanalyse/GebiedsanalyseModule';
import { AdminLayout } from './pages/AdminDashboard/AdminLayout';
import { AdminOverview } from './pages/AdminDashboard/AdminOverview';
import { CompaniesPage } from './pages/AdminDashboard/CompaniesPage';
import { CompanyDetailPage } from './pages/AdminDashboard/CompanyDetailPage';
import { ProjectsPage } from './pages/AdminDashboard/ProjectsPage';
import { ProjectDetailPage } from './pages/AdminDashboard/ProjectDetailPage';
import { UsersPage } from './pages/AdminDashboard/UsersPage';
import { UserFormPage } from './pages/AdminDashboard/UserFormPage';
import { ProjectEditPage } from './pages/AdminDashboard/ProjectEditPage';
import { CompanyEditPage } from './pages/AdminDashboard/CompanyEditPage';
import { DataSourcesPage } from './pages/AdminDashboard/DataSourcesPage';
import { ProjectWebsiteModule } from './components/projectwebsite/ProjectWebsiteModule';
import { PublicSiteLayout } from './components/public-site/PublicSiteLayout';
import { IssueRadarModule } from './components/issue-radar/IssueRadarModule';
import { ParticipationModule } from './components/participation/ParticipationModule';
import { ReportsModule } from './components/reports/ReportsModule';
import { StakeholderNetworkModule } from './components/stakeholder-network/StakeholderNetworkModule';
import { PermitsModule } from './components/permits/PermitsModule';
import { KnowledgeBaseModule } from './components/knowledge-base/KnowledgeBaseModule';
import { ProfilePage } from './pages/Profile/ProfilePage';
import { SettingsPage } from './pages/Settings/SettingsPage';

import { Module, Project, User } from './types';
import { mockProjects, mockUser, mockDashboardMetrics, mockPersonas, mockSentimentData } from './data/mockData';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { projectsAPI, dashboardAPI } from './services/api';
import { ChatPanel } from './components/ai-assistant/ChatPanel';

interface ProjectLayoutProps {
  user: User;
  projects: Project[];
  loadingProjects: boolean;
  onLogout: () => void;
}

const ProjectLayout: React.FC<ProjectLayoutProps> = ({ user, projects, loadingProjects, onLogout }) => {
  const navigate = useNavigate();
  const { projectId, module = 'dashboard' } = useParams<{ projectId: string; module?: string }>();
  const [isChatOpen, setIsChatOpen] = useState(false);

  const currentProject = projects.find(p => p.id === projectId);

  if (loadingProjects) {
    return <div className={styles.loading}>Laden...</div>;
  }

  if (!currentProject) {
    return <Navigate to="/projects" />;
  }

  const handleProjectChange = (project: Project) => {
    navigate(`/project/${project.id}/${module}`);
  };

  const handleAllProjectsClick = () => {
    navigate('/projects');
  };

  const handleModuleChange = (newModule: Module) => {
    navigate(`/project/${projectId}/${newModule}`);
  };

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  return (
    <div className={styles.app}>
      <Header
        currentProject={currentProject}
        projects={projects}
        user={user}
        onProjectChange={handleProjectChange}
        onAllProjectsClick={handleAllProjectsClick}
        onLogout={handleLogout}
      />
      
      <div className={styles.layout}>
        <Sidebar
          activeModule={module as Module}
          onModuleChange={handleModuleChange}
          enabledModules={currentProject.settings?.modules}
        />
        
        <main className={module === 'gebiedsanalyse' ? styles.mainContentWide : styles.mainContent}>
          {module === 'dashboard' && (
            <DashboardModule
              project={currentProject}
              metrics={mockDashboardMetrics}
            />
          )}

          {module === 'gebiedsanalyse' && (
            <GebiedsanalyseModule
              project={currentProject}
            />
          )}

          {module === 'sentiment' && (
            <SentimentModule
              project={currentProject}
              sentimentData={mockSentimentData}
            />
          )}
          
          {module === 'personas' && (
            <PersonasModule
              project={currentProject}
            />
          )}
          
          {module === 'strategy' && (
            <StrategyModule
              project={currentProject}
            />
          )}
          
          {module === 'media' && (
            <MiddelenModule
              project={currentProject}
            />
          )}
          
          {module === 'werkplanning' && (
            <WerkplanningModule project={currentProject} />
          )}

          {module === 'vragen' && (
            <VragenModule
              project={currentProject}
            />
          )}

          {module === 'relatiebeheer' && (
            <RelatiebeheerModule
              project={currentProject}
            />
          )}

          {module === 'projectwebsite' && (
            <ProjectWebsiteModule
              project={currentProject}
            />
          )}

          {module === 'issue-radar' && (
            <IssueRadarModule
              project={currentProject}
            />
          )}

          {module === 'participatie' && (
            <ParticipationModule
              project={currentProject}
            />
          )}

          {module === 'rapportage' && (
            <ReportsModule
              project={currentProject}
            />
          )}

          {module === 'stakeholder-netwerk' && (
            <StakeholderNetworkModule
              project={currentProject}
            />
          )}

          {module === 'vergunningen' && (
            <PermitsModule
              project={currentProject}
            />
          )}

          {module === 'kennisbank' && (
            <KnowledgeBaseModule
              project={currentProject}
            />
          )}
        </main>
      </div>
      
      {/* AI Assistant FAB */}
      {!isChatOpen && (
        <button className={styles.fab} onClick={() => setIsChatOpen(true)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
          AI Assistent
        </button>
      )}

      {/* AI Assistant Chat Panel */}
      <ChatPanel
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        projectId={currentProject.id}
        projectName={currentProject.name}
        activeModule={module}
      />
    </div>
  );
};

const ProjectSelectionRouter: React.FC<{
  projects: Project[];
  user: User;
  isAdmin: boolean;
  onLogout: () => void;
}> = ({ projects, user, isAdmin, onLogout }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (projects.length === 1 && !isAdmin) {
      navigate(`/project/${projects[0].id}/dashboard`, { replace: true });
    }
  }, [projects, isAdmin, navigate]);

  if (projects.length === 1 && !isAdmin) {
    return null;
  }

  return (
    <ProjectSelection
      projects={projects}
      user={user}
      isAdmin={isAdmin}
      onProjectSelect={() => {}}
      onLogout={onLogout}
    />
  );
};

function AppContent() {
  const { user, login, loginWithLinkedIn, logout, isAuthenticated, isAdmin, loading } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProjects();
    }
  }, [isAuthenticated]);

  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const projectsData = await projectsAPI.getMyProjects();
      // Map backend projects to frontend Project type
      const mappedProjects = projectsData.map((p: any) => ({
        id: p._id,
        name: p.name,
        description: p.description,
        shortDescription: p.shortDescription || '',
        location: p.location?.city || p.location || 'Unknown',
        locationData: p.location || null,
        startDate: p.startDate,
        endDate: p.endDate,
        status: p.status,
        headerImage: p.headerImage,
        company: p.company,
        settings: p.settings,
        metrics: p.metrics || {
          sentimentScore: 0,
          stakeholderCount: 0,
          mediaReach: 0,
          engagementRate: 0
        }
      }));
      setProjects(mappedProjects);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      // Fall back to mock data if API fails
      setProjects(mockProjects);
    } finally {
      setLoadingProjects(false);
    }
  };

  const toast = useToast();

  const handleLogin = async (email: string, password: string) => {
    const result = await login(email, password);
    if (!result.success) {
      toast.error('Inloggen mislukt', result.error || 'Controleer je e-mailadres en wachtwoord.');
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ?
            <Navigate to="/projects" /> :
            <Login onLogin={handleLogin} onLinkedInLogin={loginWithLinkedIn} />
        }
      />

      <Route
        path="/projects"
        element={
          !isAuthenticated ?
            <Navigate to="/login" /> :
            loadingProjects ?
              <div className={styles.loading}>Projecten laden...</div> :
              <ProjectSelectionRouter
                projects={projects}
                user={user!}
                isAdmin={isAdmin}
                onLogout={logout}
              />
        }
      />

      <Route
        path="/profile"
        element={
          !isAuthenticated ?
            <Navigate to="/login" /> :
            <ProfilePage />
        }
      />

      <Route
        path="/settings"
        element={
          !isAuthenticated ?
            <Navigate to="/login" /> :
            <SettingsPage />
        }
      />

      <Route
        path="/admin"
        element={
          !isAuthenticated || !isAdmin ?
            <Navigate to="/login" /> :
            <AdminLayout
              user={user!}
              onLogout={logout}
            />
        }
      >
        <Route index element={<AdminOverview />} />
        <Route path="companies" element={<CompaniesPage />} />
        <Route path="companies/new" element={<CompanyEditPage />} />
        <Route path="companies/:companyId" element={<CompanyDetailPage />} />
        <Route path="companies/:companyId/edit" element={<CompanyEditPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/new" element={<ProjectEditPage />} />
        <Route path="projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="projects/:projectId/edit" element={<ProjectEditPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="users/new" element={<UserFormPage />} />
        <Route path="users/:userId/edit" element={<UserFormPage />} />
        <Route path="datasources" element={<DataSourcesPage />} />
      </Route>

      <Route
        path="/project/:projectId/:module/*"
        element={
          !isAuthenticated ?
            <Navigate to="/login" /> :
            <ProjectLayout
              user={user!}
              projects={projects}
              loadingProjects={loadingProjects}
              onLogout={logout}
            />
        }
      />
      <Route
        path="/project/:projectId"
        element={
          !isAuthenticated ?
            <Navigate to="/login" /> :
            <ProjectLayout
              user={user!}
              projects={projects}
              loadingProjects={loadingProjects}
              onLogout={logout}
            />
        }
      />

      <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />

      <Route path="/" element={<Navigate to="/login" />} />
      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <ToastProvider>
        <Routes>
          {/* Public site route — no auth required */}
          <Route path="/site/:slug" element={<PublicSiteLayout />} />

          {/* All authenticated routes */}
          <Route path="/*" element={
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          } />
        </Routes>
      </ToastProvider>
    </Router>
  );
}

export default App;